import type { DeeplinkModule, ParsedLink } from "./types";

const MODULE_MAP: Record<string, DeeplinkModule> = {
  referalpage: "refer",
  contactus: "contactUs",
  bookmark: "bookmarks",
  subscriptions: "subscriptions",
  orders: "orders",
  selflearn: "selflearn",
  liveclass: "liveclass",
  masterclasses: "liveclass",
  doubts: "doubts",
  tests: "tests",
  "profile-update": "profileUpdate",
  profileupdate: "profileUpdate",
};

const normalize = (s?: string | null) => (s ?? "").trim().toLowerCase();

/** Supports `?module=profile-update&userId=` (value is uam_id) and malformed `?module=profile-update?userId=`. */
function extractProfileUpdateUserId(
  rawUrl: string,
  moduleRaw: string | null,
  searchParams: URLSearchParams,
): string | undefined {
  const fromQuery =
    searchParams.get("userId")?.trim() ||
    searchParams.get("userid")?.trim() ||
    undefined;
  if (fromQuery) return fromQuery;

  const moduleVal = moduleRaw ?? "";
  const embedded = /(?:^|[?&])userId=([^&?#]+)/i.exec(moduleVal);
  if (embedded?.[1]) return decodeURIComponent(embedded[1]);

  const fromRaw =
    /[?&]userId=([^&?#]+)/i.exec(rawUrl) ?? /\?userId=([^&?#]+)/i.exec(rawUrl);
  return fromRaw?.[1] ? decodeURIComponent(fromRaw[1]) : undefined;
}

export function parseLink(urlStr: string | null): ParsedLink | null {
  if (!urlStr) return null;
  try {
    const url = new URL(urlStr);

    // Module-based deep links (e.g., ?module=liveclass&grade=12&examname=1003)
    // Accept if URL has a recognized module param (isFromDeepLinking is optional).
    const moduleRaw = url.searchParams.get("module");
    let moduleKey = normalize(moduleRaw);
    // Malformed: module=profile-update?userId=xxx
    if (moduleKey.includes("?")) {
      moduleKey = moduleKey.split("?")[0] ?? moduleKey;
    }

    // Path-based fallbacks (masterclasses, doubts short links)
    const lowerPath = url.pathname.toLowerCase();
    const isFromSurgeVideos =
      url.searchParams.get("isFromSurgeVideos")?.trim() === "true";
    const isFromDeepLinking =
      url.searchParams.get("isFromDeepLinking")?.trim() === "true";

    if (!moduleKey) {
      if (lowerPath.includes("masterclasses")) {
        moduleKey = "liveclass";
      } else if (
        lowerPath.endsWith("/doubt") ||
        lowerPath.endsWith("/doubts")
      ) {
        moduleKey = "doubts";
      }
    }

    const module = moduleKey ? MODULE_MAP[moduleKey] : undefined;
    if (!module && !isFromSurgeVideos && !isFromDeepLinking) return null;

    const grade = url.searchParams.get("grade")?.trim();
    const examRaw =
      url.searchParams.get("examname")?.trim() ||
      url.searchParams.get("examName")?.trim();

    const examNum =
      examRaw != null && examRaw !== "" ? Number(examRaw) : Number.NaN;
    const examName = Number.isFinite(examNum) ? examNum : examRaw || undefined;

    const type = url.searchParams.get("type")?.trim();
    const examType =
      url.searchParams.get("examType")?.trim() ||
      url.searchParams.get("examtype")?.trim();
    const testId =
      url.searchParams.get("testId")?.trim() ||
      url.searchParams.get("testID")?.trim() ||
      url.searchParams.get("examid")?.trim();
    const questionPaperId =
      url.searchParams.get("question_paper_id")?.trim() ||
      url.searchParams.get("questionPaperId")?.trim() ||
      url.searchParams.get("paperID")?.trim() ||
      url.searchParams.get("paperId")?.trim();
    const seriesId =
      url.searchParams.get("series_id")?.trim() ||
      url.searchParams.get("testseriesAsmId")?.trim();
    const scheduleId =
      url.searchParams.get("scheduleId")?.trim() ||
      url.searchParams.get("scheduleid")?.trim();
    const cohortAsmId =
      url.searchParams.get("cohortAsmId")?.trim() ||
      url.searchParams.get("cohortasmid")?.trim();
    const courseId =
      url.searchParams.get("courseId")?.trim() ||
      url.searchParams.get("courseid")?.trim();
    const classId =
      url.searchParams.get("classId")?.trim() ||
      url.searchParams.get("classid")?.trim();
    const batchId =
      url.searchParams.get("batchId")?.trim() ||
      url.searchParams.get("batchid")?.trim();
    const subjectId = url.searchParams.get("subjectId")?.trim();
    const chapterId = url.searchParams.get("chapterId")?.trim();
    const tabName = url.searchParams.get("tabName")?.trim();
    const userId =
      module === "profileUpdate"
        ? extractProfileUpdateUserId(urlStr, moduleRaw, url.searchParams)
        : undefined;

    return {
      ...(module ? { module } : {}),
      ...(grade ? { grade } : {}),
      ...(examName !== undefined ? { examName } : {}),
      ...(type ? { type } : {}),
      ...(examType ? { examType } : {}),
      ...(testId ? { testId } : {}),
      ...(questionPaperId ? { questionPaperId } : {}),
      ...(seriesId ? { seriesId } : {}),
      ...(scheduleId ? { scheduleId } : {}),
      ...(cohortAsmId ? { cohortAsmId } : {}),
      ...(courseId ? { courseId } : {}),
      ...(classId ? { classId } : {}),
      ...(batchId ? { batchId } : {}),
      ...(subjectId ? { subjectId } : {}),
      ...(chapterId ? { chapterId } : {}),
      ...(tabName ? { tabName } : {}),
      ...(userId ? { userId } : {}),

      allParams: Object.fromEntries(url.searchParams.entries()),
    };
  } catch {
    return null;
  }
}

export function parseSignupReferralDeeplink(
  urlStr: string | null,
): { referralCode: string } | null {
  if (!urlStr) return null;
  try {
    const url = new URL(urlStr);
    if (!url.pathname.toLowerCase().endsWith("/signup")) return null;
    const code = url.searchParams.get("referralCode")?.trim();
    return code ? { referralCode: code } : null;
  } catch {
    return null;
  }
}
